import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download } from 'lucide-react';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { PhotoLightbox, PhotoThumb } from '@/components/ui/PhotoLightbox';
import { AttendanceTrendGraph } from '@/components/admin/reports/AttendanceTrendGraph';
import { adminService } from '@/services/admin';
import { exportService } from '@/services/export';
import { getAttendanceDenominator, getAttendancePercentage } from '@/pages/admin/attendanceMetricUtils';
import { resolvePersonPhoto } from '@/utils/avatars';

export default function AdminTeacherPersonReportPage() {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const monthKey = `${year}-${String(month).padStart(2, '0')}`;

  const { data: monthData, isLoading: monthLoading } = useQuery({
    queryKey: ['teacher-attendance-history', teacherId, monthKey],
    queryFn: () => adminService.getTeacherAttendanceHistory(teacherId!, monthKey),
    enabled: !!teacherId,
  });

  const { data: yearData, isLoading: yearLoading } = useQuery({
    queryKey: ['teacher-attendance-yearly', teacherId, year],
    queryFn: () => adminService.getTeacherYearlyHistory(teacherId!, year),
    enabled: !!teacherId,
  });

  const person = monthData?.teacher || yearData?.teacher;
  const photoSrc = resolvePersonPhoto(person, 'teacher');
  const monthSummary = monthData?.summary || { present: 0, absent: 0, late: 0, leave: 0 };
  const monthExpected = getAttendanceDenominator(monthSummary);
  const yearSummary = yearData?.summary || { present: 0, absent: 0, late: 0, leave: 0, expected_records: 0 };
  const yearExpected = getAttendanceDenominator(yearSummary);
  const graphData = useMemo(
    () =>
      (yearData?.months || []).map((item: { month: string; present: number; absent: number; percentage: number }) => ({
        label: item.month.slice(0, 3),
        present: item.present,
        absent: item.absent,
        rate: item.percentage,
      })),
    [yearData],
  );

  const downloadPdf = () => {
    const rows = (monthData?.records || []).map((record: { date: string; status: string }) => [record.date, record.status]);
    exportService.generatePDF({
      title: `Teacher attendance — ${format(new Date(year, month - 1, 1), 'MMMM yyyy')}`,
      subtitle: 'Teacher report',
      headers: ['Date', 'Status'],
      rows,
      summary: [
        { label: 'Present', value: `${getAttendancePercentage(monthSummary.present, monthExpected)}%` },
        { label: 'Absent', value: monthSummary.absent },
        { label: 'Late', value: monthSummary.late },
        { label: 'Expected', value: monthExpected },
      ],
    });
  };

  return (
    <DashboardShell title="Teacher report" role="admin">
      <div className="space-y-6">
        <Button variant="secondary" onClick={() => navigate('/admin/reports/teachers')}>
          ← Teacher reports
        </Button>

        <Card className="flex flex-col items-center gap-6 p-6 md:flex-row">
          <PhotoThumb
            src={photoSrc}
            sizeClass="h-24 w-24"
            onClick={() => setPreviewSrc(photoSrc)}
          />
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold text-slate-800">{person?.name || 'Teacher report'}</h2>
            <div className="mt-3 flex flex-wrap justify-center gap-3 md:justify-start">
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800">
                Present {getAttendancePercentage(monthSummary.present, monthExpected)}%
              </span>
              <span className="rounded-full bg-rose-100 px-3 py-1 text-sm font-medium text-rose-800">
                Absent {monthSummary.absent}
              </span>
              <span className="rounded-full bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-800">
                Year {getAttendancePercentage(yearSummary.present, yearExpected)}%
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <select aria-label="Month" className="rounded-lg border-slate-300 text-sm" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, index) => index + 1).map((value) => (
                <option key={value} value={value}>{format(new Date(2000, value - 1, 1), 'MMMM')}</option>
              ))}
            </select>
            <select aria-label="Year" className="rounded-lg border-slate-300 text-sm" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[year - 1, year, year + 1].filter((value, index, list) => list.indexOf(value) === index).map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
            <Button onClick={downloadPdf} className="gap-2 bg-indigo-600 text-white hover:bg-indigo-700" disabled={monthLoading}>
              <Download size={16} />
              Download PDF
            </Button>
          </div>
        </Card>

        <AttendanceTrendGraph
          title="This year"
          caption="Present, absent, and rate by month"
          data={graphData}
          isLoading={yearLoading}
        />

        <Card className="p-6">
          <h3 className="mb-4 font-bold text-slate-800">{format(new Date(year, month - 1, 1), 'MMMM yyyy')}</h3>
          {monthLoading ? (
            <div className="h-40 animate-pulse rounded-xl bg-slate-50" />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-slate-500">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(monthData?.records || []).map((record: { id?: string; date: string; status: string }) => (
                    <tr key={record.id || record.date}>
                      <td className="px-4 py-2">{record.date}</td>
                      <td className="px-4 py-2">{record.status}</td>
                    </tr>
                  ))}
                  {(!monthData?.records || monthData.records.length === 0) && (
                    <tr>
                      <td colSpan={2} className="px-4 py-6 text-center text-slate-400">No marks this month</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
      <PhotoLightbox src={previewSrc} onClose={() => setPreviewSrc(null)} />
    </DashboardShell>
  );
}
